import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import MysticalParticles from './MysticalParticles';
import { ChevronLeft, ChevronRight, Calendar, Clock, MapPin } from 'lucide-react';

const EventCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    const loadEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch('https://api.4fingerstudios.com/api/mystavil/events', { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Failed to fetch events: ${response.status}`);
        }
        const data = await response.json();

        const mapped = (Array.isArray(data) ? data : []).map(e => {
          const id = e.id || e.Id;
          const title = e.eventTitle || e.EventTitle || '';
          const rawDate = e.eventDate || e.EventDate;
          const date = rawDate ? new Date(rawDate) : null;
          const time = e.eventTime || e.EventTime || '';
          const location = e.eventLocation || e.EventLocation || '';
          const description = e.eventDescription || e.EventDescription || '';
          const type = (e.eventType || e.EventType || '').toLowerCase();

          return { id, title, date, time, location, description, type };
        }).filter(ev => ev.date instanceof Date && !isNaN(ev.date));

        mapped.sort((a, b) => a.date - b.date);
        setEvents(mapped);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError('Failed to load events.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
    return () => controller.abort();
  }, []);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    return events.filter(event => 
      event.date.toDateString() === date.toDateString()
    );
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(prevDate.getMonth() + direction);
      return newDate;
    });
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'celebration': return 'bg-purple-500';
      case 'contest': return 'bg-pink-500';
      case 'social': return 'bg-blue-500';
      case 'workshop': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const days = getDaysInMonth(currentDate);
  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Magical Background */}
      <div className="fixed inset-0 -z-10">
        <img 
          src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
          alt="Mystveil Township Map" 
          className="w-full h-full object-cover opacity-30 blur-sm" 
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
      </div>

      {/* Magical Particles */}
      <MysticalParticles />

      {/* Navigation */}
      <Navbar />

      {/* Main Content */}
      <main className="flex-grow px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="mystical-card mb-8">
            <h1 className="mystical-title text-black mb-8">
              Event Calendar
            </h1>
            
            <div className="mystical-card bg-white bg-opacity-95 mb-8">
              <p className="mystical-text text-black text-lg">
                Stay up to date with all the exciting events happening in Mystveil Township! 
                From art contests to community meetups, there's always something magical happening.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Calendar */}
            <div className="lg:col-span-2">
              <div className="mystical-card">
                {loading && (
                  <div className="mystical-card bg-white bg-opacity-95 mb-4">
                    <p className="mystical-text text-gray-600">Loading events...</p>
                  </div>
                )}
                {error && (
                  <div className="mystical-card bg-white bg-opacity-95 mb-4">
                    <p className="mystical-text text-red-600">{error}</p>
                  </div>
                )}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="mystical-subtitle text-black text-2xl">
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h2>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => navigateMonth(-1)}
                      className="mystical-button p-2"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      onClick={() => navigateMonth(1)}
                      className="mystical-button p-2"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-2 mb-4">
                  {dayNames.map(day => (
                    <div key={day} className="p-3 text-center font-bold text-gray-800 bg-gray-100 rounded-md">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-2">
                  {days.map((day, index) => {
                    const dayEvents = day ? getEventsForDate(day) : [];
                    const isToday = day && day.toDateString() === new Date().toDateString();
                    const isSelected = day && selectedDate && day.toDateString() === selectedDate.toDateString();
                    
                    return (
                      <div
                        key={index}
                        className={`
                          aspect-square p-2 cursor-pointer transition-all duration-200 rounded-md border-2
                          ${day ? 'bg-white hover:bg-purple-100 hover:border-purple-300' : 'bg-gray-50'}
                          ${isToday ? 'bg-yellow-200 border-yellow-400' : ''}
                          ${isSelected ? 'bg-purple-200 border-purple-400' : ''}
                          ${day ? 'border-gray-300' : 'border-transparent'}
                        `}
                        onClick={() => day && setSelectedDate(day)}
                      >
                        {day && (
                          <div className="h-full flex flex-col">
                            <span className={`text-lg font-bold ${isToday ? 'text-purple-900' : 'text-black'}`}>
                              {day.getDate()}
                            </span>
                            {dayEvents.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {dayEvents.slice(0, 3).map(event => (
                                  <div
                                    key={event.id}
                                    className={`w-2 h-2 rounded-full ${getEventTypeColor(event.type)}`}
                                    title={event.title}
                                  />
                                ))}
                                {dayEvents.length > 3 && (
                                  <div className="w-2 h-2 rounded-full bg-gray-400 text-xs flex items-center justify-center" title={`+${dayEvents.length - 3} more`}>
                                    <span className="text-xs text-white font-bold">+</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Event Details */}
            <div className="lg:col-span-1">
              <div className="mystical-card">
                <h3 className="mystical-subtitle text-black text-xl mb-6">
                  {selectedDate ? `Events for ${selectedDate.toLocaleDateString()}` : 'Select a Date'}
                </h3>

                {selectedDateEvents.length > 0 ? (
                  <div className="space-y-4">
                    {selectedDateEvents.map(event => (
                      <div key={event.id} className="mystical-card bg-white bg-opacity-95">
                        <div className="flex items-start space-x-3">
                          <div className={`w-3 h-3 rounded-full mt-1 ${getEventTypeColor(event.type)}`} />
                          <div className="flex-1">
                            <h4 className="mystical-subtitle text-black text-lg mb-2">
                              {event.title}
                            </h4>
                            <div className="space-y-1 text-sm">
                              <div className="flex items-center space-x-2">
                                <Clock size={14} className="text-gray-600" />
                                <span className="mystical-text text-gray-600">{event.time}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <MapPin size={14} className="text-gray-600" />
                                <span className="mystical-text text-gray-600">{event.location}</span>
                              </div>
                            </div>
                            <p className="mystical-text text-gray-700 text-sm mt-2">
                              {event.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : selectedDate ? (
                  <div className="mystical-card bg-white bg-opacity-95">
                    <p className="mystical-text text-gray-600 text-center">
                      No events scheduled for this date.
                    </p>
                  </div>
                ) : (
                  <div className="mystical-card bg-white bg-opacity-95">
                    <p className="mystical-text text-gray-600 text-center">
                      Click on a date to view events.
                    </p>
                  </div>
                )}
              </div>

              {/* Upcoming Events */}
              <div className="mystical-card mt-8">
                <h3 className="mystical-subtitle text-black text-xl mb-6">
                  Upcoming Events
                </h3>
                <div className="space-y-3">
                  {events.slice(0, 3).map(event => (
                    <div key={event.id} className="mystical-card bg-white bg-opacity-95">
                      <div className="flex items-start space-x-3">
                        <div className={`w-3 h-3 rounded-full mt-1 ${getEventTypeColor(event.type)}`} />
                        <div className="flex-1">
                          <h4 className="mystical-subtitle text-black text-sm mb-1">
                            {event.title}
                          </h4>
                          <p className="mystical-text text-gray-600 text-xs">
                            {event.date && event.date.toLocaleDateString()} • {event.time}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/" className="mystical-button">
              Return Home
            </Link>
            <Link to="/news" className="mystical-button">
              Latest News
            </Link>
            <Link to="/guide" className="mystical-button">
              View Guides
            </Link>
          </div>
        </div>
      </main>

      {/* Mystical Footer */}
      <footer className="mystical-nav py-6 mt-auto">
        <div className="container mx-auto px-4 text-center">
          <p className="mystical-text font-bold">
            © 2025 4fingerstudios <br /> 
            © 2024 Reckless Dreams. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default EventCalendar;
